import { useLayoutEffect, useRef, useState } from "react";
import Tooltip from "@mui/material/Tooltip";

import type { CertificationItem } from "components/Screens/Marketplace/Customer/types";
import verifiedIcon from "components/Screens/Marketplace/Customer/assets/common/verified.svg";
import PlgIcon from "plg/shell/PlgIcon";

import "components/Screens/Marketplace/Customer/ProfileSection/ProfileContent.scss";

const tooltipComponentsProps = {
  tooltip: {
    sx: {
      bgcolor: "#333333",
      color: "white",
      fontSize: "12px",
      maxWidth: "240px",
    },
  },
};

// Prototype .tip on a .cert-edge card: 8px above the card, 12px in from its left edge.
const certTipSlotProps = {
  tooltip: { className: "cert-tip" },
  popper: { modifiers: [{ name: "offset", options: { offset: [12, 0] } }] },
};

function CertificationTitle({ title, withTooltip }: { title: string; withTooltip: boolean }) {
  const titleRef = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return undefined;
    const check = () => setIsTruncated(el.scrollHeight - el.clientHeight > 1);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [title]);

  const titleEl = (
    <div ref={titleRef} className="certification-card__title">
      {title}
    </div>
  );

  if (!withTooltip || !isTruncated) return titleEl;

  return (
    <Tooltip title={title} slotProps={tooltipComponentsProps}>
      {titleEl}
    </Tooltip>
  );
}

/**
 * Prototype .cert / .cert-edge: an Edge Edu certification is tinted, with the verified badge in its corner. One with a
 * description (product 2026-10-24) opens a tip on hover: its title, then a paragraph per item. That tip shows the full
 * title, so it stands in for the badge's and the clamped title's own tooltips.
 */
function CertificationCard({ certification }: { certification: CertificationItem }) {
  const paragraphs = certification.isEdgeEdu && certification.description?.length ? certification.description : null;
  const badge = (
    <div className="certification-card__verified-icon">
      <img src={verifiedIcon} alt="Edge Edu Certified" />
    </div>
  );
  const card = (
    <div
      className={`certification-card${certification.isEdgeEdu ? " certification-card--edge" : ""}`}
      tabIndex={paragraphs ? 0 : undefined}
    >
      {certification.isEdgeEdu &&
        (paragraphs ? (
          badge
        ) : (
          <Tooltip
            title="Earned through Edge Edu—our internal program designed to upskill and certify talent."
            slotProps={tooltipComponentsProps}
          >
            {badge}
          </Tooltip>
        ))}
      <div className="certification-card__icon">
        <PlgIcon name="award" size={18} />
      </div>
      <div className="certification-card__content">
        <div className="certification-card__title-row">
          <CertificationTitle title={certification.title} withTooltip={!paragraphs} />
        </div>
        <div className="certification-card__date">{certification.issuedDate}</div>
      </div>
    </div>
  );
  if (!paragraphs) return card;
  return (
    <Tooltip
      title={
        <>
          <b>{certification.title}</b>
          {paragraphs.map((text, i) => (
            <p key={`${i}-${text}`}>{text}</p>
          ))}
        </>
      }
      placement="top-start"
      enterTouchDelay={0}
      leaveTouchDelay={4000}
      slotProps={certTipSlotProps}
    >
      {card}
    </Tooltip>
  );
}

interface CertificationsSectionProps {
  certifications: CertificationItem[];
}

function CertificationsSection({ certifications }: CertificationsSectionProps) {
  if (!certifications || certifications.length === 0) {
    return null;
  }

  // Edge Edu certifications first; otherwise the order is kept (a stable sort).
  const ordered = [...certifications].sort((a, b) => Number(!!b.isEdgeEdu) - Number(!!a.isEdgeEdu));

  return (
    <div id="certifications" className="profile-content__section">
      <div className="profile-content__title mb-075">Certifications</div>
      <div className="certifications-grid">
        {ordered.map((certification) => (
          <CertificationCard key={`${certification.title}-${certification.issuedDate}`} certification={certification} />
        ))}
      </div>
    </div>
  );
}

export default CertificationsSection;
